/**
 * Agreement (`/agreement`, TMMRSL-0031, H §6.3 / §7.2): the reseller's current agreement and its
 * history (`GET /agreement`), the **Standing** card (`GET /standing`) and a plain-language
 * explainer of the minimum commitment. Read-only — the product admin edits agreements. No current
 * agreement (or `409 PAYOUT_METHOD_NOT_CONFIGURED`) → the same empty state as Payouts. The minimum
 * commitment is the one single-currency figure: it is in the agreement's own `currency`, shown
 * beside it. The agreement carries no price card since TMMAPI-0154 (MM-D-136): the platform sets
 * the cards, and "Your price cards" links to them. Under Standing, **Standing emails** lists the
 * standing emails sent to the reseller (TMMRSL-0046).
 */
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  resellerAdminApi,
  type ResellerAgreementView,
  type ResellerStanding,
} from '@/api/resellerAdmin';
import { ApiErrorState } from '@/components/ApiErrorState';
import { MoneyCell } from '@/components/cells';
import { StatusChip } from '@/components/status/StatusChip';
import { DefinitionField, DefinitionList } from '@/components/DefinitionList';
import { PageHeader } from '@/components/PageHeader';
import { moneyIn } from '@/components/moneyIn';
import { useRemote } from '@/components/useRemote';
import { isPayoutMethodNotConfigured } from '@/features/payouts/notConfigured';
import { formatCivilDate, formatCivilOrDash } from '@/i18n/format';
import { todayCivil } from '@/i18n/periods';
import { enumLabel } from '@/i18n/enumLabel';
import { useLocale } from '@/i18n/useLocale';
import { NotPricedState } from '@/tenant/NotPricedState';
import { StandingEmails } from './StandingEmails';

const loadAgreement = () => resellerAdminApi.getAgreement();
const loadStanding = () => resellerAdminApi.getStanding();

/** A standing month (`YYYY-MM`, or a date in it) as "Aug 2026". */
function monthLabel(value: string, locale: string): string {
  const month = value.slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(month)) return value;
  try {
    return formatCivilDate(`${month}-01`, locale, { year: 'numeric', month: 'short' });
  } catch {
    return value;
  }
}

export function AgreementScreen() {
  const { t } = useTranslation();
  const agreement = useRemote(loadAgreement);

  const notConfigured =
    (agreement.status === 'error' && isPayoutMethodNotConfigured(agreement.error)) ||
    (agreement.status === 'ready' && agreement.data?.current === null);

  return (
    <Box>
      <PageHeader id="agreement" />
      {notConfigured ? <NotPricedState /> : null}
      {agreement.status === 'error' && !notConfigured ? (
        <ApiErrorState what={t('errors.what.agreement')} error={agreement.error} onRetry={agreement.reload} />
      ) : null}
      {agreement.status === 'loading' && !agreement.data ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={220} />
        </Box>
      ) : null}
      {agreement.status === 'ready' && agreement.data?.current ? (
        <Stack spacing={2.5}>
          <CurrentAgreement current={agreement.data.current} />
          <StandingCard />
          <StandingEmails />
          <AgreementHistory history={agreement.data.history} />
        </Stack>
      ) : null}
    </Box>
  );
}

function CurrentAgreement({ current }: { current: ResellerAgreementView }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const money = (minor: number | null) =>
    minor == null ? dash : moneyIn(minor, current.currency, locale, dash);

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {t('agreement.currentTitle')}
      </Typography>
      <DefinitionList>
        <DefinitionField label={t('money.currency')}>{current.currency || dash}</DefinitionField>
        <DefinitionField label={t('agreement.field.cadence')}>
          {current.payoutCadence ? enumLabel(t, 'statements.cadence', current.payoutCadence) : dash}
        </DefinitionField>
        <DefinitionField label={t('agreement.field.method')}>
          {current.payoutMethod ? enumLabel(t, 'statements.method', current.payoutMethod) : dash}
        </DefinitionField>
        <DefinitionField label={t('agreement.field.minPayout')}>
          {money(current.minPayoutMinor)}
        </DefinitionField>
        <DefinitionField label={t('agreement.field.taxDocs')}>
          {current.taxDocsStatus ? enumLabel(t, 'statements.tax', current.taxDocsStatus) : dash}
        </DefinitionField>
        <DefinitionField label={t('agreement.field.monthlyMinimum')}>
          {money(current.monthlyMinimumMinor)}
        </DefinitionField>
        <DefinitionField label={t('agreement.field.probationMonths')}>
          {current.probationMonths == null
            ? dash
            : t('agreement.months', { count: current.probationMonths })}
        </DefinitionField>
        <DefinitionField label={t('agreement.field.effectiveFrom')}>
          {formatCivilOrDash(current.effectiveFrom, locale, dash)}
        </DefinitionField>
        <DefinitionField label={t('agreement.field.officialLaunchOn')}>
          {current.officialLaunchOn
            ? formatCivilOrDash(current.officialLaunchOn, locale, dash)
            : t('agreement.launch.notSet')}
        </DefinitionField>
      </DefinitionList>
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        {current.probationMonths == null
          ? t('agreement.explainerUnknownProbation', {
              minimum: money(current.monthlyMinimumMinor),
            })
          : current.probationMonths <= 0
            ? t('agreement.explainerNoProbation', {
                minimum: money(current.monthlyMinimumMinor),
              })
            : t('agreement.explainer', {
                minimum: money(current.monthlyMinimumMinor),
                count: current.probationMonths,
              })}
      </Typography>
      <Button component={RouterLink} to="/rate-cards" sx={{ mt: 1.5 }}>
        {t('pages.rateCards.title')}
      </Button>
    </Paper>
  );
}

function StandingCard() {
  const { t } = useTranslation();
  const standing = useRemote(loadStanding);

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {t('agreement.standing.title')}
      </Typography>
      {standing.status === 'error' ? (
        <ApiErrorState error={standing.error} onRetry={standing.reload} />
      ) : null}
      {standing.status === 'loading' && !standing.data ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={120} />
        </Box>
      ) : null}
      {standing.status === 'ready' && standing.data ? (
        <StandingReady standing={standing.data} />
      ) : null}
    </Paper>
  );
}

function StandingReady({ standing }: { standing: ResellerStanding }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  // Nothing is measured before official launch, nor at all without it (UI-010).
  const notMeasured =
    standing.officialLaunchOn === null || standing.officialLaunchOn.slice(0, 10) > todayCivil();

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <StatusChip domain="standing" status={standing.state} />
      </Box>
      {standing.outOfBand ? (
        <Alert severity="info">{t(`agreement.standing.outOfBand.${standing.outOfBand}`)}</Alert>
      ) : null}
      <DefinitionList>
        <DefinitionField label={t('agreement.standing.officialLaunchOn')}>
          {standing.officialLaunchOn
            ? formatCivilOrDash(standing.officialLaunchOn, locale, dash)
            : t('agreement.launch.notSet')}
        </DefinitionField>
        {standing.probationDeadline ? (
          <DefinitionField label={t('agreement.standing.probationDeadline')}>
            {formatCivilOrDash(standing.probationDeadline, locale, dash)}
          </DefinitionField>
        ) : null}
        {standing.nextNoticeOn ? (
          <DefinitionField label={t('agreement.standing.nextNoticeOn')}>
            {formatCivilOrDash(standing.nextNoticeOn, locale, dash)}
          </DefinitionField>
        ) : null}
      </DefinitionList>
      {standing.platformManagedSince ? (
        <Typography variant="body2">
          {t('agreement.standing.platformManagedSince', {
            date: formatCivilOrDash(standing.platformManagedSince, locale, dash),
          })}
        </Typography>
      ) : null}
      {notMeasured ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('agreement.launch.notMeasured')}
        </Typography>
      ) : null}
      {standing.probationStartedOn || standing.probationEndsOn ? (
        <Typography variant="body2">
          {t('agreement.standing.probation', {
            start: formatCivilOrDash(standing.probationStartedOn, locale, dash),
            end: formatCivilOrDash(standing.probationEndsOn, locale, dash),
          })}
        </Typography>
      ) : null}
      {standing.months.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('agreement.standing.noMonths')}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={t('agreement.standing.monthsTitle')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('agreement.standing.col.month')}</TableCell>
                <TableCell align="right">{t('agreement.standing.col.minimum')}</TableCell>
                <TableCell align="right">{t('agreement.standing.col.earned')}</TableCell>
                <TableCell align="right">{t('agreement.standing.col.shortfall')}</TableCell>
                <TableCell>{t('agreement.standing.col.charge')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {standing.months.map((month) => {
                return (
                  <TableRow key={month.month}>
                    <TableCell component="th" scope="row">
                      {monthLabel(month.month, locale)}
                    </TableCell>
                    <MoneyCell minor={month.minimumMinor} currency={month.currency} />
                    <MoneyCell minor={month.earnedMinor} currency={month.currency} />
                    <MoneyCell minor={month.shortfallMinor} currency={month.currency} />
                    <TableCell>
                      <StatusChip domain="resellerCharge" status={month.chargeStatus} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Stack>
  );
}

function AgreementHistory({ history }: { history: ResellerAgreementView[] }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {t('agreement.historyTitle')}
      </Typography>
      {history.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('agreement.historyEmpty')}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={t('agreement.historyTitle')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('agreement.field.effectiveFrom')}</TableCell>
                <TableCell>{t('agreement.field.cadence')}</TableCell>
                <TableCell>{t('money.currency')}</TableCell>
                <TableCell align="right">{t('agreement.field.monthlyMinimum')}</TableCell>
                <TableCell>{t('agreement.field.endedAt')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((entry) => (
                <TableRow key={`${entry.effectiveFrom}-${entry.endedAt ?? ''}`}>
                  <TableCell component="th" scope="row">
                    {formatCivilOrDash(entry.effectiveFrom, locale, dash)}
                  </TableCell>
                  <TableCell>
                    {entry.payoutCadence
                      ? enumLabel(t, 'statements.cadence', entry.payoutCadence)
                      : dash}
                  </TableCell>
                  <TableCell>{entry.currency || dash}</TableCell>
                  <MoneyCell minor={entry.monthlyMinimumMinor} currency={entry.currency} />
                  <TableCell>
                    {entry.endedAt ? formatCivilOrDash(entry.endedAt, locale, dash) : t('agreement.current')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
